"use client";

import { useCallback, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import {
  usePaperTradingStore,
  type OrderFormData,
  selectOpenPositions,
  selectOpenOrders,
} from "@/stores/paperTradingStore";

const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || "http://localhost:3001";

interface ApiOptions {
  method?: "GET" | "POST" | "PUT" | "DELETE";
  body?: unknown;
}

async function apiRequest<T>(endpoint: string, options: ApiOptions = {}): Promise<T> {
  const { method = "GET", body } = options;

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method,
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: "Request failed" }));
    throw new Error(error.message || `API error: ${response.status}`);
  }

  return response.json();
}

export function usePaperTrading() {
  const { session } = useAuth();
  const isAuthenticated = !!session?.user;
  const userId = session?.user?.id;
  const store = usePaperTradingStore();

  // Fetch account on login
  const fetchAccount = useCallback(async () => {
    if (!userId) return;

    store.setLoadingAccount(true);
    store.setError(null);

    try {
      const account = await apiRequest<typeof store.account>(
        "/api/paper-trading/account"
      );
      store.setAccount(account);
    } catch (error) {
      store.setError(error instanceof Error ? error.message : "Failed to fetch account");
    } finally {
      store.setLoadingAccount(false);
    }
  }, [userId, store]);

  // Fetch positions
  const fetchPositions = useCallback(async () => {
    if (!userId) return;

    store.setLoadingPositions(true);

    try {
      const positions = await apiRequest<typeof store.positions>(
        "/api/paper-trading/positions/open"
      );
      store.setPositions(positions);
    } catch (error) {
      console.error("Failed to fetch positions:", error);
    } finally {
      store.setLoadingPositions(false);
    }
  }, [userId, store]);

  // Fetch orders
  const fetchOrders = useCallback(async () => {
    if (!userId) return;

    store.setLoadingOrders(true);

    try {
      const orders = await apiRequest<typeof store.orders>(
        "/api/paper-trading/orders?limit=50"
      );
      store.setOrders(orders);
    } catch (error) {
      console.error("Failed to fetch orders:", error);
    } finally {
      store.setLoadingOrders(false);
    }
  }, [userId, store]);

  // Fetch trades
  const fetchTrades = useCallback(async () => {
    if (!userId) return;

    store.setLoadingTrades(true);

    try {
      const response = await apiRequest<{ trades: typeof store.trades }>(
        "/api/paper-trading/trades?limit=100"
      );
      store.setTrades(response.trades);
    } catch (error) {
      console.error("Failed to fetch trades:", error);
    } finally {
      store.setLoadingTrades(false);
    }
  }, [userId, store]);

  // Submit order
  const submitOrder = useCallback(
    async (orderData: OrderFormData, currentPrice: number) => {
      if (!userId) {
        store.setError("Please log in to place trades");
        return null;
      }

      store.setSubmittingOrder(true);
      store.setError(null);

      try {
        const result = await apiRequest<{
          order: (typeof store.orders)[0];
          position: (typeof store.positions)[0] | null;
          balanceUpdate: { newBalance: number; newMarginUsed: number };
          realizedPnl: number;
        }>(`/api/paper-trading/orders?currentPrice=${currentPrice}`, {
          method: "POST",
          body: {
            asset: store.currentPrices["BTC/USD"] > 0 ? "BTC/USD" : "BTC/USD", // Use selected market
            type: orderData.type,
            side: orderData.side,
            size: parseFloat(orderData.size),
            price: orderData.type === "limit" ? parseFloat(orderData.price) : undefined,
            leverage: orderData.leverage,
            reduceOnly: orderData.reduceOnly,
          },
        });

        // Update store with results
        if (result.order) {
          store.addOrder(result.order);
        }
        if (result.position) {
          store.addPosition(result.position);
        }
        if (result.balanceUpdate) {
          store.updateBalance(
            result.balanceUpdate.newBalance,
            result.balanceUpdate.newMarginUsed
          );
        }

        store.resetOrderForm();
        return result;
      } catch (error) {
        store.setError(error instanceof Error ? error.message : "Failed to submit order");
        return null;
      } finally {
        store.setSubmittingOrder(false);
      }
    },
    [userId, store]
  );

  // Cancel order
  const cancelOrder = useCallback(
    async (orderId: string) => {
      if (!userId) return;

      try {
        await apiRequest(`/api/paper-trading/orders/${orderId}`, {
          method: "DELETE",
        });
        store.removeOrder(orderId);
      } catch (error) {
        store.setError(error instanceof Error ? error.message : "Failed to cancel order");
      }
    },
    [userId, store]
  );

  // Close position
  const closePosition = useCallback(
    async (positionId: string, currentPrice: number) => {
      if (!userId) return null;

      try {
        const result = await apiRequest<{
          position: (typeof store.positions)[0];
          balanceUpdate: { newBalance: number; newMarginUsed: number };
          realizedPnl: number;
        }>(
          `/api/paper-trading/positions/${positionId}/close?currentPrice=${currentPrice}`,
          {
            method: "POST",
          }
        );

        store.removePosition(positionId);
        if (result.balanceUpdate) {
          store.updateBalance(
            result.balanceUpdate.newBalance,
            result.balanceUpdate.newMarginUsed
          );
        }

        return result;
      } catch (error) {
        store.setError(
          error instanceof Error ? error.message : "Failed to close position"
        );
        return null;
      }
    },
    [userId, store]
  );

  // Update position TP/SL
  const updatePositionTpSl = useCallback(
    async (
      positionId: string,
      takeProfitPrice: number | null,
      stopLossPrice: number | null
    ) => {
      if (!userId) return;

      try {
        const updated = await apiRequest<(typeof store.positions)[0]>(
          `/api/paper-trading/positions/${positionId}`,
          {
            method: "PUT",
            body: { takeProfitPrice, stopLossPrice },
          }
        );
        store.updatePosition(positionId, updated);
      } catch (error) {
        store.setError(
          error instanceof Error ? error.message : "Failed to update position"
        );
      }
    },
    [userId, store]
  );

  // Reset account
  const resetAccount = useCallback(async () => {
    if (!userId) return;

    try {
      const account = await apiRequest<typeof store.account>(
        "/api/paper-trading/account/reset",
        { method: "POST" }
      );
      store.setAccount(account);
      store.setPositions([]);
      store.setOrders([]);
      store.setTrades([]);
    } catch (error) {
      store.setError(error instanceof Error ? error.message : "Failed to reset account");
    }
  }, [userId, store]);

  // Initialize on login
  useEffect(() => {
    if (isAuthenticated && userId) {
      fetchAccount();
      fetchPositions();
      fetchOrders();
      fetchTrades();
    } else {
      store.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, userId]);

  return {
    // State
    account: store.account,
    positions: store.positions,
    orders: store.orders,
    trades: store.trades,
    openPositions: selectOpenPositions(store),
    openOrders: selectOpenOrders(store),
    currentPrices: store.currentPrices,
    orderForm: store.orderForm,
    activeTab: store.activeTab,
    selectedPositionId: store.selectedPositionId,

    // Loading states
    isLoadingAccount: store.isLoadingAccount,
    isLoadingPositions: store.isLoadingPositions,
    isLoadingOrders: store.isLoadingOrders,
    isLoadingTrades: store.isLoadingTrades,
    isSubmittingOrder: store.isSubmittingOrder,

    // Error
    error: store.error,

    // Actions
    fetchAccount,
    fetchPositions,
    fetchOrders,
    fetchTrades,
    submitOrder,
    cancelOrder,
    closePosition,
    updatePositionTpSl,
    resetAccount,

    // Store actions
    setOrderForm: store.setOrderForm,
    resetOrderForm: store.resetOrderForm,
    setActiveTab: store.setActiveTab,
    selectPosition: store.selectPosition,
    updatePrice: store.updatePrice,
    updatePrices: store.updatePrices,
    clearError: () => store.setError(null),
  };
}
